import { Box, Card, CardContent, Typography, useTheme } from "@mui/material";
import { useTenantTranslation } from "hooks/formatters";
import { useMemo } from "react";
import ScrapTrackingViewportPlot from "components/scrapTracking/ScrapTrackingViewportPlot";
import { Layout } from "plotly.js";
import { HeatSeries } from "src/store/api/generatedApi";
import { useFormatNumber } from "src/utils/formatNumber";
import { ChunkData } from "components/scrapTracking/helpers";

interface Props {
  heatsData: { series: HeatSeries[] } | undefined;
  start: Date;
  end: Date;
  chunks: ChunkData<unknown>[];
}

export const HeatCoverageTimelinePlot = ({
  heatsData,
  start,
  end,
  chunks,
}: Props) => {
  const { t } = useTenantTranslation();
  const formatNumber = useFormatNumber();
  const theme = useTheme();

  const loadingShapes = useMemo(() => {
    return chunks
      .filter((chunk) => chunk.value.status !== "success")
      .map((chunk) => ({
        type: "rect" as const,
        xref: "x" as const,
        yref: "paper" as const,
        x0: new Date(chunk.start),
        x1: new Date(chunk.end),
        y0: 0,
        y1: 1,
        fillcolor: "grey",
        opacity: 0.15,
        layer: "below" as const,
        line: { width: 0 },
      }));
  }, [chunks]);

  const { stats, plotData } = useMemo(() => {
    if (!heatsData?.series) {
      return {
        stats: { total: 0, covered: 0, pct: 0 },
        plotData: [],
      };
    }

    // Sort heats by start time
    const sortedHeats = [...heatsData.series].sort(
      (a, b) =>
        new Date(a.start_timestamp).getTime() -
        new Date(b.start_timestamp).getTime()
    );

    let coveredCount = 0;
    const total = sortedHeats.length;

    // Merge consecutive heats of the same type if gap < 1 minute
    const GAP_THRESHOLD_MS = 60 * 1000;

    interface Span {
      start: string;
      end: string;
    }
    const coveredSpans: Span[] = [];
    const missingSpans: Span[] = [];

    sortedHeats.forEach((heat) => {
      const hasLiftData = heat.drops.length > 0;
      const startTime = heat.start_timestamp;
      const endTime = heat.tap_timestamp ?? heat.start_timestamp;
      const spans = hasLiftData ? coveredSpans : missingSpans;

      if (hasLiftData) {
        coveredCount++;
      }

      // Check if we can merge with the last span
      const lastSpan = spans[spans.length - 1];
      if (lastSpan) {
        const lastEndMs = new Date(lastSpan.end).getTime();
        const currentStartMs = new Date(startTime).getTime();
        if (currentStartMs - lastEndMs < GAP_THRESHOLD_MS) {
          // Extend the last span
          lastSpan.end = endTime;
          return;
        }
      }

      // Start a new span
      spans.push({ start: startTime, end: endTime });
    });

    // Convert spans to plot data with Date objects for timezone shifting
    const coveredX: Date[] = [];
    const coveredY: number[] = [];
    const missingX: Date[] = [];
    const missingY: number[] = [];

    coveredSpans.forEach((span) => {
      const spanStart = new Date(span.start);
      const spanEnd = new Date(span.end);
      coveredX.push(spanStart, spanStart, spanEnd, spanEnd);
      coveredY.push(0, 1, 1, 0);
    });

    missingSpans.forEach((span) => {
      const spanStart = new Date(span.start);
      const spanEnd = new Date(span.end);
      missingX.push(spanStart, spanStart, spanEnd, spanEnd);
      missingY.push(0, 1, 1, 0);
    });

    return {
      stats: {
        total,
        covered: coveredCount,
        pct: total > 0 ? (coveredCount / total) * 100 : 0,
      },
      plotData: [
        {
          x: coveredX,
          y: coveredY,
          type: "scatter" as const,
          mode: "lines" as const,
          name: t("coveredHeats"),
          line: {
            shape: "linear" as const,
            color: "#4caf50",
            width: 1,
          },
          fill: "tozeroy" as const,
          fillcolor: "#4caf5040",
          hoverinfo: "x" as const,
        },
        {
          x: missingX,
          y: missingY,
          type: "scatter" as const,
          mode: "lines" as const,
          name: t("missingData"),
          line: { shape: "linear" as const, color: "#f44336", width: 1 },
          fill: "tozeroy" as const,
          fillcolor: "#f4433640",
          hoverinfo: "x" as const,
        },
      ],
    };
  }, [heatsData, t]);

  const layout: Partial<Layout> = useMemo(
    () => ({
      xaxis: {
        range: [start, end],
        type: "date",
        showgrid: true,
        gridcolor: theme.palette.grey[100],
      },
      yaxis: {
        showticklabels: false,
        range: [-0.1, 1.1],
        fixedrange: true,
        showgrid: false,
      },
      margin: { t: 20, r: 20, l: 20, b: 40 },
      height: 200,
      showlegend: true,
      legend: { orientation: "h", y: 1.15 },
      hovermode: "x unified",
      dragmode: "pan",
      shapes: loadingShapes,
    }),
    [start, end, loadingShapes, theme.palette.grey]
  );

  return (
    <Card>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          {t("heatCoverageTimeline")}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          {`${formatNumber(1)(stats.pct)}% (${stats.covered} / ${stats.total} ${t(
            "heats"
          )})`}
        </Typography>
        <Box sx={{ height: 200 }}>
          <ScrapTrackingViewportPlot
            data={plotData}
            layout={layout}
            style={{ width: "100%", height: "100%" }}
            config={{
              displayModeBar: false,
              responsive: true,
              scrollZoom: false,
            }}
          />
        </Box>
      </CardContent>
    </Card>
  );
};
